import { tick } from 'svelte';
import { get } from 'svelte/store';
import type { PressHoldInfo, PressHoldOptions } from '@Shelly/shelly-design-system';
import { edit, initialEdit } from 'src/stores/ui/edit_lists';
import {
	isLongPressEditAvailable,
	longPressEditAvailable,
} from 'src/stores/features/dev/long-press-edit';
import { app, os } from 'src/stores/session/native-bridge';
import { hapticFeedback } from 'src/services/native_container_requests_ios';
import { hapticFeedbackAndroid } from 'src/services/native_container_requests_android';

export type EnterListEditFromLongPressOptions = {
	/** Room-devices "pure reorder" path also sets `initialEdit` (matches kebab Reorder room items). */
	initialEdit?: boolean;
};

/**
 * Enter shared rearrange / edit mode from a touch-and-hold shortcut.
 * No-ops when the Dev Modal long-press-edit flag is off or edit is already active.
 */
export const enterListEditFromLongPress = (options: EnterListEditFromLongPressOptions = {}): boolean => {
	if (!isLongPressEditAvailable()) return false;
	if (get(edit)) return false;

	if (options.initialEdit) {
		initialEdit.set(true);
	}
	edit.set(true);

	try {
		if (get(app)) {
			if (get(os) === 'IOS') {
				void hapticFeedback('selection');
			} else if (get(os) === 'ANDROID') {
				void hapticFeedbackAndroid('selection');
			}
		}
	} catch {
		// Native bridge may be unavailable in browser / web builds
	}

	return true;
};

const DND_ITEM_SELECTOR = '[data-dnd-item]';

const findDndZoneItem = (from: EventTarget | null): HTMLElement | null => {
	if (!(from instanceof Element)) return null;
	return from.closest<HTMLElement>(DND_ITEM_SELECTOR);
};

const pointerCoords = (
	event: Event | undefined,
): { clientX: number; clientY: number; target: EventTarget | null } | null => {
	if (!event) return null;

	const source = event instanceof CustomEvent ? event.detail : event;
	if (!(source instanceof Event)) return null;

	if (typeof TouchEvent !== 'undefined' && source instanceof TouchEvent) {
		const touch = source.touches[0] ?? source.changedTouches[0];
		if (!touch) return null;
		return { clientX: touch.clientX, clientY: touch.clientY, target: source.target };
	}

	if (source instanceof MouseEvent) {
		return { clientX: source.clientX, clientY: source.clientY, target: source.target };
	}

	return null;
};

const waitForDndZoneUnlock = async (): Promise<void> => {
	await tick();
	await new Promise<void>((resolve) => {
		requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
	});
};

let pickupGeneration = 0;

/** Drop the pending synthetic mousedown (grip pointer-up before pickup ran). */
export const cancelScheduledDndItemPickup = (): void => {
	pickupGeneration += 1;
};

const dispatchDndItemMousedown = (dndItem: HTMLElement, clientX: number, clientY: number): void => {
	dndItem.dispatchEvent(
		new MouseEvent('mousedown', {
			bubbles: true,
			cancelable: true,
			clientX,
			clientY,
			screenX: clientX,
			screenY: clientY,
			button: 0,
			buttons: 1,
			view: typeof window !== 'undefined' ? window : undefined,
		}),
	);
};

const settleSiblingMotion = (dndItem: HTMLElement): void => {
	const listRoot = dndItem.parentElement;
	if (!listRoot) return;

	for (const el of listRoot.children) {
		if (!(el instanceof HTMLElement)) continue;
		for (const animation of el.getAnimations({ subtree: true })) {
			try {
				animation.finish();
			} catch {
				animation.cancel();
			}
		}
	}
};

/**
 * svelte-dnd-action only starts a drag from mousedown/touchstart after `dragDisabled`
 * flips. Long-press arms edit mid-gesture, so we synthesize a mousedown on the
 * CardGrid item once the zone has reconfigured. Window touchmove listeners then
 * follow the same finger.
 */
export const scheduleDndItemPickup = async (info: PressHoldInfo): Promise<void> => {
	const dndItem = findDndZoneItem(info.node) ?? info.node.parentElement;
	if (!dndItem) return;

	const generation = pickupGeneration;
	await waitForDndZoneUnlock();
	if (generation !== pickupGeneration) return;
	settleSiblingMotion(dndItem);
	dispatchDndItemMousedown(dndItem, info.clientX, info.clientY);
};

/**
 * Same pickup as long-press, for grip / handle `handle-drag-start`.
 * The grip stops the original pointer event so the zone never sees it.
 */
export const scheduleDndItemPickupFromEvent = async (event: Event | undefined): Promise<void> => {
	const coords = pointerCoords(event);
	if (!coords) return;

	const dndItem = findDndZoneItem(coords.target);
	if (!dndItem) return;

	const generation = pickupGeneration;
	await waitForDndZoneUnlock();
	if (generation !== pickupGeneration) return;
	settleSiblingMotion(dndItem);
	dispatchDndItemMousedown(dndItem, coords.clientX, coords.clientY);
};

export type BuildListPressHoldOptions = EnterListEditFromLongPressOptions & {
	/** Extra gate (e.g. room-devices All Devices route has no reorder). */
	enabled?: boolean;
	/** Called after edit mode is entered so the holder can unlock dragDisabled. */
	onEntered?: () => void;
};

/**
 * Shared `use:pressHold` options for ListMenuCardV2 / device cards.
 * Prefer a reactive `$:` block that rebuilds these when `$edit` / `$longPressEditAvailable` change,
 * because the action reads `enabled` via `update()`.
 */
export const buildListPressHoldOptions = (
	editActive: boolean,
	longPressAvailable: boolean,
	options: BuildListPressHoldOptions = {},
): PressHoldOptions => ({
	enabled: (options.enabled ?? true) && !editActive && longPressAvailable,
	onPressHold: () => {
		if (!enterListEditFromLongPress({ initialEdit: options.initialEdit })) return;
		options.onEntered?.();
	},
	onArmedMove: (info) => {
		void scheduleDndItemPickup(info);
	},
});

/** Re-export for call sites that already import from this helper module. */
export { longPressEditAvailable };
